import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, getErrorMessage } from '@/lib/api';
import { Card, Button, Input, Select, Badge } from '@/components/ui';
import { Search, Film, Plus, Check, AlertTriangle, ArrowLeft, ExternalLink } from 'lucide-react';

interface OmdbSearchResult {
  Title: string;
  Year: string;
  imdbID: string;
  Type: string;
  Poster: string;
}

interface OmdbMovieDetail {
  raw: {
    Title: string;
    Year: string;
    Rated: string;
    Released: string;
    Runtime: string;
    Genre: string;
    Director: string;
    Actors: string;
    Plot: string;
    Poster: string;
    imdbRating: string;
    imdbID: string;
  };
  normalized: {
    imdbId: string;
    title: string;
    overview: string;
    releaseDate: string | null;
    runtime: number | null;
    rating: string;
    posterUrl: string | null;
    genres: string[];
    director: string | null;
    cast: string[];
    imdbRating: number | null;
  };
  alreadyImported: boolean;
  existingMovieId: string | null;
}

interface OmdbStatus {
  configured: boolean;
}

interface ImportedMovie {
  id: string;
  title: string;
  slug: string;
}

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
];

export function OmdbImportPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchYear, setSearchYear] = useState('');
  const [selectedImdbId, setSelectedImdbId] = useState<string | null>(null);
  const [importStatus, setImportStatus] = useState<'draft' | 'published'>('draft');
  const [importNowPlaying, setImportNowPlaying] = useState(false);
  const [importComingSoon, setImportComingSoon] = useState(false);

  // Check if OMDB is configured
  const { data: omdbStatus, isLoading: statusLoading } = useQuery<OmdbStatus>({
    queryKey: ['omdb-status'],
    queryFn: () => apiGet('/admin/omdb/status'),
  });

  // Search movies
  const { data: searchResults, isLoading: searchLoading, refetch: doSearch } = useQuery<{
    results: OmdbSearchResult[];
    totalResults: number;
  }>({
    queryKey: ['omdb-search', searchQuery, searchYear],
    queryFn: () => {
      const params = new URLSearchParams({ query: searchQuery });
      if (searchYear) params.set('year', searchYear);
      return apiGet(`/admin/omdb/search?${params}`);
    },
    enabled: false,
  });

  // Get movie details
  const { data: movieDetail, isLoading: detailLoading } = useQuery<OmdbMovieDetail>({
    queryKey: ['omdb-movie', selectedImdbId],
    queryFn: () => apiGet(`/admin/omdb/movie/${selectedImdbId}`),
    enabled: !!selectedImdbId,
  });

  // Import movie
  const importMutation = useMutation({
    mutationFn: (imdbId: string) =>
      apiPost<ImportedMovie>(`/admin/omdb/import/${imdbId}`, {
        status: importStatus,
        isNowPlaying: importNowPlaying,
        isComingSoon: importComingSoon,
      }),
    onSuccess: (movie) => {
      queryClient.invalidateQueries({ queryKey: ['movies'] });
      queryClient.invalidateQueries({ queryKey: ['omdb-movie', selectedImdbId] });
      navigate(`/movies/${movie.id}`);
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      doSearch();
    }
  };

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
      </div>
    );
  }

  if (!omdbStatus?.configured) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/movies')}
            className="p-2 rounded-lg hover:bg-midnight-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Import from OMDB</h1>
            <p className="text-gray-400 mt-1">Search and import movies from the Open Movie Database</p>
          </div>
        </div>

        <Card>
          <div className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">OMDB API Not Configured</h2>
            <p className="text-gray-400 mb-4">
              To import movies from OMDB, you need to set up an API key.
            </p>
            <div className="bg-midnight-100 rounded-lg p-4 text-left max-w-md mx-auto">
              <p className="text-sm text-gray-300 mb-2">
                1. Get a free API key at{' '}
                <a
                  href="https://www.omdbapi.com/apikey.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-400 hover:underline"
                >
                  omdbapi.com
                </a>
              </p>
              <p className="text-sm text-gray-300 mb-2">
                2. Add it to your <code className="text-gold">.env</code> file:
              </p>
              <code className="text-sm text-gold block bg-midnight rounded p-2">
                OMDB_API_KEY=your_key_here
              </code>
              <p className="text-sm text-gray-300 mt-2">
                3. Restart the API server
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/movies')}
          className="p-2 rounded-lg hover:bg-midnight-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Import from OMDB</h1>
          <p className="text-gray-400 mt-1">Search and import movies from the Open Movie Database</p>
        </div>
      </div>

      {/* Search */}
      <Card padding="md">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search movie title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
          </div>
          <div className="w-full md:w-32">
            <Input
              placeholder="Year"
              value={searchYear}
              onChange={(e) => setSearchYear(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={searchLoading || !searchQuery.trim()}>
            {searchLoading ? 'Searching...' : 'Search'}
          </Button>
        </form>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Search Results */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">Search Results</h2>
          <Card padding="none">
            {!searchResults ? (
              <div className="p-8 text-center text-gray-500">
                Search for a movie to see results
              </div>
            ) : searchResults.results.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No movies found
              </div>
            ) : (
              <div className="divide-y divide-midnight-100">
                {searchResults.results.map((movie) => (
                  <button
                    key={movie.imdbID}
                    onClick={() => setSelectedImdbId(movie.imdbID)}
                    className={`w-full p-4 flex items-center gap-4 text-left transition-colors ${
                      selectedImdbId === movie.imdbID
                        ? 'bg-midnight-100'
                        : 'hover:bg-midnight-50'
                    }`}
                  >
                    <div className="w-12 h-16 rounded overflow-hidden bg-midnight-100 flex-shrink-0">
                      {movie.Poster && movie.Poster !== 'N/A' ? (
                        <img
                          src={movie.Poster}
                          alt={movie.Title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="h-5 w-5 text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{movie.Title}</p>
                      <p className="text-sm text-gray-400">{movie.Year}</p>
                    </div>
                    <a
                      href={`https://www.imdb.com/title/${movie.imdbID}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-400 hover:text-amber-400"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Movie Detail */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">Movie Details</h2>
          <Card>
            {!selectedImdbId ? (
              <div className="p-8 text-center text-gray-500">
                Select a movie to see details
              </div>
            ) : detailLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold mx-auto" />
              </div>
            ) : movieDetail ? (
              <div className="space-y-4">
                {/* Poster and Basic Info */}
                <div className="flex gap-4">
                  <div className="w-24 h-36 rounded overflow-hidden bg-midnight-100 flex-shrink-0">
                    {movieDetail.normalized.posterUrl ? (
                      <img
                        src={movieDetail.normalized.posterUrl}
                        alt={movieDetail.normalized.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="h-8 w-8 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white">
                      {movieDetail.normalized.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <Badge variant="default">{movieDetail.normalized.rating}</Badge>
                      {movieDetail.normalized.runtime && (
                        <span className="text-sm text-gray-400">
                          {movieDetail.normalized.runtime} min
                        </span>
                      )}
                      {movieDetail.normalized.imdbRating && (
                        <span className="text-sm text-yellow-500">
                          IMDB: {movieDetail.normalized.imdbRating}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {movieDetail.normalized.genres.map((genre) => (
                        <Badge key={genre} variant="secondary">
                          {genre}
                        </Badge>
                      ))}
                    </div>
                    {movieDetail.normalized.director && (
                      <p className="text-sm text-gray-400 mt-2">
                        Director: {movieDetail.normalized.director}
                      </p>
                    )}
                  </div>
                </div>

                {/* Overview */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-1">Overview</h4>
                  <p className="text-sm text-gray-300 line-clamp-4">
                    {movieDetail.normalized.overview || 'No overview available'}
                  </p>
                </div>

                {/* Import Options */}
                {movieDetail.alreadyImported ? (
                  <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <Check className="h-5 w-5 text-green-500" />
                    <span className="text-green-400">Already imported</span>
                    <button
                      onClick={() => navigate(`/movies/${movieDetail.existingMovieId}`)}
                      className="ml-auto text-sm text-amber-400 hover:underline"
                    >
                      View movie
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="border-t border-midnight-100 pt-4 space-y-4">
                      <h4 className="text-sm font-medium text-white">Import Options</h4>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Status</label>
                          <Select
                            options={statusOptions}
                            value={importStatus}
                            onChange={(e) => setImportStatus(e.target.value as 'draft' | 'published')}
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={importNowPlaying}
                            onChange={(e) => setImportNowPlaying(e.target.checked)}
                            className="rounded border-gray-600 bg-midnight-100 text-gold focus:ring-gold"
                          />
                          <span className="text-sm text-gray-300">Now Playing</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={importComingSoon}
                            onChange={(e) => setImportComingSoon(e.target.checked)}
                            className="rounded border-gray-600 bg-midnight-100 text-gold focus:ring-gold"
                          />
                          <span className="text-sm text-gray-300">Coming Soon</span>
                        </label>
                      </div>
                    </div>

                    <Button
                      onClick={() => importMutation.mutate(movieDetail.normalized.imdbId)}
                      disabled={importMutation.isPending}
                      className="w-full"
                    >
                      {importMutation.isPending ? (
                        'Importing...'
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Import Movie
                        </>
                      )}
                    </Button>

                    {importMutation.error && (
                      <p className="text-sm text-red-400">
                        {getErrorMessage(importMutation.error)}
                      </p>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                Movie not found
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
